import React, { useCallback, useState } from 'react';
import { DatabaseIcon, SparklesIcon } from 'lucide-react';
import { DuplicateList } from '../components/DuplicateList';
import { ComparisonView } from '../components/ComparisonView';
import { MergePreview } from '../components/MergePreview';
import { MultiRecordMergeView } from '../components/MultiRecordMergeView';
import { MultiMergePreview } from '../components/MultiMergePreview';
import { DuplicateSet, CustomerRecord } from '../components/DuplicateCard';
import { HeatmapMergeView } from '../components/HeatmapMergeView';
// Mock data for duplicate sets
const mockDuplicateSets: DuplicateSet[] = [{
  id: '1',
  primaryName: 'John Smith',
  secondaryName: 'Jonathan Smith',
  confidenceScore: 95,
  conflictCount: 3,
  matchCount: 5,
  records: [{
    id: 'REC-001',
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@email.com',
    phone: '(555) 123-4567',
    address: '123 Oak Street, Austin, TX 78701',
    loanAmount: 425000,
    applicationDate: '2024-01-15',
    ssn: '***-**-1234',
    createdAt: '2024-01-15T10:30:00Z',
    source: 'Web Application'
  }, {
    id: 'REC-002',
    firstName: 'Jonathan',
    lastName: 'Smith',
    email: 'jsmith@gmail.com',
    phone: '(555) 123-4567',
    address: '123 Oak St, Austin, TX 78701',
    loanAmount: 450000,
    applicationDate: '2024-01-20',
    ssn: '***-**-1234',
    createdAt: '2024-01-20T14:45:00Z',
    source: 'Loan Officer Entry'
  }]
}, {
  id: '2',
  primaryName: 'Maria Garcia',
  secondaryName: 'Maria L. Garcia',
  confidenceScore: 92,
  conflictCount: 2,
  matchCount: 6,
  records: [{
    id: 'REC-003',
    firstName: 'Maria',
    lastName: 'Garcia',
    email: 'maria.garcia@outlook.com',
    phone: '(555) 987-6543',
    address: '456 Pine Avenue, Dallas, TX 75201',
    loanAmount: 350000,
    applicationDate: '2024-02-01',
    ssn: '***-**-5678',
    createdAt: '2024-02-01T09:15:00Z',
    source: 'Partner Referral'
  }, {
    id: 'REC-004',
    firstName: 'Maria',
    lastName: 'Garcia',
    email: 'mgarcia@yahoo.com',
    phone: '(555) 987-6543',
    address: '456 Pine Avenue, Dallas, TX 75201',
    loanAmount: 350000,
    applicationDate: '2024-02-01',
    ssn: '***-**-5678',
    createdAt: '2024-02-05T11:20:00Z',
    source: 'Web Application'
  }]
}, {
  id: '3',
  primaryName: 'Robert Johnson',
  secondaryName: 'Bob Johnson',
  confidenceScore: 87,
  conflictCount: 4,
  matchCount: 4,
  records: [{
    id: 'REC-005',
    firstName: 'Robert',
    lastName: 'Johnson',
    email: 'robert.j@company.com',
    phone: '(555) 456-7890',
    address: '789 Elm Drive, Houston, TX 77001',
    loanAmount: 575000,
    applicationDate: '2024-01-28',
    ssn: '***-**-9012',
    createdAt: '2024-01-28T16:00:00Z',
    source: 'Salesforce Import'
  }, {
    id: 'REC-006',
    firstName: 'Bob',
    lastName: 'Johnson',
    email: 'bob.johnson@email.com',
    phone: '(555) 456-7891',
    address: '789 Elm Dr, Houston, TX 77001',
    loanAmount: 550000,
    applicationDate: '2024-02-10',
    ssn: '***-**-9012',
    createdAt: '2024-02-10T08:30:00Z',
    source: 'Web Application'
  }]
}, {
  id: '4',
  primaryName: 'Sarah Williams',
  secondaryName: 'Sara Williams',
  confidenceScore: 98,
  conflictCount: 1,
  matchCount: 7,
  records: [{
    id: 'REC-007',
    firstName: 'Sarah',
    lastName: 'Williams',
    email: 'sarah.williams@email.com',
    phone: '(555) 321-0987',
    address: '321 Maple Lane, San Antonio, TX 78201',
    loanAmount: 280000,
    applicationDate: '2024-02-15',
    ssn: '***-**-3456',
    createdAt: '2024-02-15T13:45:00Z',
    source: 'Web Application'
  }, {
    id: 'REC-008',
    firstName: 'Sara',
    lastName: 'Williams',
    email: 'sarah.williams@email.com',
    phone: '(555) 321-0987',
    address: '321 Maple Lane, San Antonio, TX 78201',
    loanAmount: 280000,
    applicationDate: '2024-02-15',
    ssn: '***-**-3456',
    createdAt: '2024-02-16T10:00:00Z',
    source: 'Loan Officer Entry'
  }]
}, {
  id: '5',
  primaryName: 'Michael Chen',
  secondaryName: 'Mike Chen',
  confidenceScore: 89,
  conflictCount: 3,
  matchCount: 5,
  records: [{
    id: 'REC-009',
    firstName: 'Michael',
    lastName: 'Chen',
    email: 'michael.chen@techcorp.com',
    phone: '(555) 654-3210',
    address: '555 Cedar Blvd, Fort Worth, TX 76101',
    loanAmount: 625000,
    applicationDate: '2024-02-20',
    ssn: '***-**-7890',
    createdAt: '2024-02-20T11:30:00Z',
    source: 'Partner Referral'
  }, {
    id: 'REC-010',
    firstName: 'Mike',
    lastName: 'Chen',
    email: 'm.chen@gmail.com',
    phone: '(555) 654-3210',
    address: '555 Cedar Boulevard, Fort Worth, TX 76101',
    loanAmount: 600000,
    applicationDate: '2024-02-22',
    ssn: '***-**-7890',
    createdAt: '2024-02-22T15:15:00Z',
    source: 'Web Application'
  }]
}];
// Add new mock data with 3+ duplicates
const mockMultiDuplicateSets: DuplicateSet[] = [{
  id: '6',
  primaryName: 'Jennifer Martinez',
  secondaryName: '4 duplicates',
  confidenceScore: 94,
  conflictCount: 5,
  matchCount: 3,
  records: [{
    id: 'REC-011',
    firstName: 'Jennifer',
    lastName: 'Martinez',
    email: 'jennifer.martinez@email.com',
    phone: '(555) 111-2222',
    address: '100 Main Street, Austin, TX 78701',
    loanAmount: 380000,
    applicationDate: '2024-01-10',
    ssn: '***-**-4321',
    createdAt: '2024-01-10T09:00:00Z',
    source: 'Web Application'
  }, {
    id: 'REC-012',
    firstName: 'Jenny',
    lastName: 'Martinez',
    email: 'jmartinez@gmail.com',
    phone: '(555) 111-2222',
    address: '100 Main St, Austin, TX 78701',
    loanAmount: 400000,
    applicationDate: '2024-01-15',
    ssn: '***-**-4321',
    createdAt: '2024-01-15T14:30:00Z',
    source: 'Loan Officer Entry'
  }, {
    id: 'REC-013',
    firstName: 'Jennifer',
    lastName: 'Martinez',
    email: 'jen.martinez@company.com',
    phone: '(555) 111-2223',
    address: '100 Main Street, Austin, TX 78701',
    loanAmount: 380000,
    applicationDate: '2024-01-10',
    ssn: '***-**-4321',
    createdAt: '2024-01-18T11:00:00Z',
    source: 'Partner Referral'
  }, {
    id: 'REC-014',
    firstName: 'Jennifer',
    lastName: 'Martinez',
    email: 'jennifer.martinez@email.com',
    phone: '(555) 111-2222',
    address: '100 Main St, Austin, Texas 78701',
    loanAmount: 400000,
    applicationDate: '2024-01-20',
    ssn: '***-**-4321',
    createdAt: '2024-01-20T16:45:00Z',
    source: 'Salesforce Import'
  }]
}, {
  id: '7',
  primaryName: 'David Thompson',
  secondaryName: '3 duplicates',
  confidenceScore: 91,
  conflictCount: 4,
  matchCount: 4,
  records: [{
    id: 'REC-015',
    firstName: 'David',
    lastName: 'Thompson',
    email: 'david.thompson@email.com',
    phone: '(555) 333-4444',
    address: '200 Oak Avenue, Dallas, TX 75201',
    loanAmount: 520000,
    applicationDate: '2024-02-05',
    ssn: '***-**-5555',
    createdAt: '2024-02-05T10:15:00Z',
    source: 'Web Application'
  }, {
    id: 'REC-016',
    firstName: 'Dave',
    lastName: 'Thompson',
    email: 'd.thompson@company.com',
    phone: '(555) 333-4444',
    address: '200 Oak Ave, Dallas, TX 75201',
    loanAmount: 550000,
    applicationDate: '2024-02-08',
    ssn: '***-**-5555',
    createdAt: '2024-02-08T13:20:00Z',
    source: 'Loan Officer Entry'
  }, {
    id: 'REC-017',
    firstName: 'David',
    lastName: 'Thompson',
    email: 'david.t@gmail.com',
    phone: '(555) 333-4445',
    address: '200 Oak Avenue, Dallas, TX 75201',
    loanAmount: 520000,
    applicationDate: '2024-02-05',
    ssn: '***-**-5555',
    createdAt: '2024-02-10T09:30:00Z',
    source: 'Partner Referral'
  }]
}, {
  id: '8',
  primaryName: 'Amanda Wilson',
  secondaryName: '5 duplicates',
  confidenceScore: 88,
  conflictCount: 6,
  matchCount: 2,
  records: [{
    id: 'REC-018',
    firstName: 'Amanda',
    lastName: 'Wilson',
    email: 'amanda.wilson@email.com',
    phone: '(555) 777-8888',
    address: '300 Pine Road, Houston, TX 77001',
    loanAmount: 295000,
    applicationDate: '2024-01-25',
    ssn: '***-**-6666',
    createdAt: '2024-01-25T08:00:00Z',
    source: 'Web Application'
  }, {
    id: 'REC-019',
    firstName: 'Amanda',
    lastName: 'Wilson',
    email: 'awilson@company.com',
    phone: '(555) 777-8888',
    address: '300 Pine Rd, Houston, TX 77001',
    loanAmount: 300000,
    applicationDate: '2024-01-28',
    ssn: '***-**-6666',
    createdAt: '2024-01-28T12:15:00Z',
    source: 'Loan Officer Entry'
  }, {
    id: 'REC-020',
    firstName: 'Mandy',
    lastName: 'Wilson',
    email: 'mandy.w@gmail.com',
    phone: '(555) 777-8889',
    address: '300 Pine Road, Houston, TX 77001',
    loanAmount: 295000,
    applicationDate: '2024-01-25',
    ssn: '***-**-6666',
    createdAt: '2024-02-01T10:30:00Z',
    source: 'Partner Referral'
  }, {
    id: 'REC-021',
    firstName: 'Amanda',
    lastName: 'Wilson',
    email: 'amanda.wilson@email.com',
    phone: '(555) 777-8888',
    address: '300 Pine Road, Houston, Texas 77001',
    loanAmount: 310000,
    applicationDate: '2024-02-05',
    ssn: '***-**-6666',
    createdAt: '2024-02-05T14:45:00Z',
    source: 'Salesforce Import'
  }, {
    id: 'REC-022',
    firstName: 'Amanda',
    lastName: 'Wilson',
    email: 'a.wilson@outlook.com',
    phone: '(555) 777-8888',
    address: '300 Pine Rd, Houston, TX 77001',
    loanAmount: 300000,
    applicationDate: '2024-02-08',
    ssn: '***-**-6666',
    createdAt: '2024-02-08T11:00:00Z',
    source: 'Web Application'
  }]
}];
// Combine all duplicate sets
const allDuplicateSets = [...mockDuplicateSets, ...mockMultiDuplicateSets];
type FieldSelection = {
  [fieldName: string]: 'left' | 'right' | null;
};
type MultiFieldSelection = {
  [fieldName: string]: string | null; // record ID
};
const initialFieldSelections: FieldSelection = {
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  address: null,
  loanAmount: null,
  applicationDate: null,
  ssn: null
};
export function DuplicateMergeScreen() {
  const [duplicateSets, setDuplicateSets] = useState<DuplicateSet[]>(allDuplicateSets);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [fieldSelections, setFieldSelections] = useState<FieldSelection>(initialFieldSelections);
  const [multiFieldSelections, setMultiFieldSelections] = useState<MultiFieldSelection>(initialFieldSelections);
  const [primaryRecordId, setPrimaryRecordId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [mergeStatus, setMergeStatus] = useState<'idle' | 'merging' | 'success' | 'error'>('idle');
  const [lastMergedSet, setLastMergedSet] = useState<DuplicateSet | null>(null);
  const selectedSet = duplicateSets.find(set => set.id === selectedSetId);
  const isMultiRecord = selectedSet && selectedSet.records.length > 2;
  const handleSelectSet = useCallback((id: string) => {
    setSelectedSetId(id);
    setFieldSelections(initialFieldSelections);
    setMultiFieldSelections(initialFieldSelections);
    setPrimaryRecordId(null);
    setShowPreview(false);
    setMergeStatus('idle');
  }, []);
  const handleFieldSelect = useCallback((fieldName: string, side: 'left' | 'right') => {
    setFieldSelections(prev => ({
      ...prev,
      [fieldName]: side
    }));
    setShowPreview(true);
  }, []);
  const handleMultiFieldSelect = useCallback((fieldName: string, recordId: string) => {
    setMultiFieldSelections(prev => ({
      ...prev,
      [fieldName]: recordId
    }));
    setShowPreview(true);
  }, []);
  const handleSelectPrimary = useCallback((recordId: string) => {
    setPrimaryRecordId(recordId);
  }, []);
  const handleMerge = useCallback(() => {
    if (!selectedSet) return;
    setMergeStatus('merging');
    setTimeout(() => {
      setLastMergedSet(selectedSet);
      setDuplicateSets(prev => prev.filter(set => set.id !== selectedSetId));
      setMergeStatus('success');
    }, 1500);
  }, [selectedSet, selectedSetId]);
  const handleUndo = useCallback(() => {
    if (!lastMergedSet) return;
    setDuplicateSets(prev => [...prev, lastMergedSet]);
    setSelectedSetId(lastMergedSet.id);
    setLastMergedSet(null);
    setMergeStatus('idle');
    setShowPreview(false);
  }, [lastMergedSet]);
  const handleClosePreview = useCallback(() => {
    if (mergeStatus === 'success') {
      setSelectedSetId(null);
      setShowPreview(false);
      setMergeStatus('idle');
    } else {
      setShowPreview(false);
    }
  }, [mergeStatus]);
  return <div className="min-h-screen w-full bg-gray-100 flex flex-col" data-id="element-358">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-359">
        <div className="flex items-center justify-between" data-id="element-360">
          <div className="flex items-center gap-3" data-id="element-361">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center" data-id="element-362">
              <DatabaseIcon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-363" />
            </div>
            <div data-id="element-364">
              <h1 className="text-xl font-bold text-gray-900" data-id="element-365">
                Record Merge Center
              </h1>
              <p className="text-sm text-gray-600" data-id="element-366">
                Identify and merge duplicate customer records
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-367">
            <span className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full" data-id="element-368">
              <SparklesIcon className="w-4 h-4 text-blue-500" aria-hidden="true" data-id="element-369" />
              AI-Powered Matching
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" data-id="element-370">
        {/* Sidebar - Duplicate List */}
        <DuplicateList duplicateSets={duplicateSets} selectedSetId={selectedSetId} onSelectSet={handleSelectSet} searchQuery={searchQuery} onSearchChange={setSearchQuery} data-id="element-371" />

        {/* Main Area */}
        <main className="flex-1 overflow-hidden flex flex-col" data-id="element-372">
          {selectedSet ? isMultiRecord ? <HeatmapMergeView duplicateSet={selectedSet} fieldSelections={multiFieldSelections} onFieldSelect={handleMultiFieldSelect} onSelectPrimary={handleSelectPrimary} primaryRecordId={primaryRecordId} showPreview={showPreview} data-id="element-373" /> : <ComparisonView duplicateSet={selectedSet} fieldSelections={fieldSelections} onFieldSelect={handleFieldSelect} showPreview={showPreview} data-id="element-374" /> : <div className="flex-1 flex items-center justify-center bg-gray-50" data-id="element-375">
              <div className="text-center max-w-md px-6" data-id="element-376">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-377">
                  <DatabaseIcon className="w-10 h-10 text-blue-500" aria-hidden="true" data-id="element-378" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-379">
                  Select a Duplicate Set
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-380">
                  Choose a duplicate set from the list on the left to compare
                  records and merge them into a single, accurate customer
                  profile.
                </p>
                <div className="flex flex-col gap-3 text-sm text-gray-500" data-id="element-381">
                  <div className="flex items-center gap-2" data-id="element-382">
                    <span className="w-2 h-2 rounded-full bg-green-500" aria-hidden="true" data-id="element-383"></span>
                    High confidence matches are highlighted in green
                  </div>
                  <div className="flex items-center gap-2" data-id="element-384">
                    <span className="w-2 h-2 rounded-full bg-yellow-500" aria-hidden="true" data-id="element-385"></span>
                    Conflicts require your decision
                  </div>
                  <div className="flex items-center gap-2" data-id="element-386">
                    <span className="w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" data-id="element-387"></span>
                    AI recommendations are marked with a star
                  </div>
                </div>
              </div>
            </div>}
        </main>
      </div>

      {/* Merge Preview Panel */}
      {selectedSet && (isMultiRecord ? <MultiMergePreview records={selectedSet.records} fieldSelections={multiFieldSelections} primaryRecordId={primaryRecordId} onMerge={handleMerge} onCancel={handleClosePreview} isVisible={showPreview} mergeStatus={mergeStatus} onUndo={handleUndo} data-id="element-388" /> : <MergePreview leftRecord={selectedSet.records[0]} rightRecord={selectedSet.records[1]} fieldSelections={fieldSelections} onMerge={handleMerge} onCancel={handleClosePreview} isVisible={showPreview} mergeStatus={mergeStatus} onUndo={handleUndo} data-id="element-389" />)}
    </div>;
}